class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input =
      this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  getLineItem(index) {
    return (
      document.getElementById(`CartItem-${index}`) || document.getElementById(`CartDrawer-Item-${index}`)
    );
  }

  hideAllRemoveHints() {
    document.querySelectorAll('.cart-remove-hint.is-visible').forEach((hint) => {
      hint.classList.remove('is-visible');
      hint.setAttribute('aria-hidden', 'true');
      const removeButton = hint.closest('cart-remove-button');
      if (removeButton) removeButton.removeAttribute('aria-describedby');
    });
  }

  hideRemoveHint(index) {
    const lineItem = this.getLineItem(index);
    const hint = lineItem?.querySelector('.cart-remove-hint');
    if (!hint) return;

    hint.classList.remove('is-visible');
    hint.setAttribute('aria-hidden', 'true');
    hint.closest('cart-remove-button')?.removeAttribute('aria-describedby');
  }

  showRemoveHint(event, index, message) {
    event.target.setCustomValidity('');
    this.resetQuantityInput(index);
    this.hideAllRemoveHints();

    const lineItem = this.getLineItem(index);
    const removeButton = lineItem?.querySelector('cart-remove-button');
    const hint = removeButton?.querySelector('.cart-remove-hint');
    if (!hint) return;

    hint.querySelector('.cart-remove-hint__text').textContent = message;
    hint.classList.add('is-visible');
    hint.setAttribute('aria-hidden', 'false');
    removeButton.setAttribute('aria-describedby', hint.id);

    if (this.removeHintDismissHandler) {
      document.removeEventListener('click', this.removeHintDismissHandler);
    }

    this.removeHintDismissHandler = (clickEvent) => {
      if (removeButton.contains(clickEvent.target)) return;
      this.hideRemoveHint(index);
      document.removeEventListener('click', this.removeHintDismissHandler);
      this.removeHintDismissHandler = undefined;
    };

    setTimeout(() => {
      document.addEventListener('click', this.removeHintDismissHandler);
    }, 0);
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message =
        inputValue === 0
          ? window.cartStrings.removeHint
          : window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      if (inputValue === 0) {
        this.showRemoveHint(event, index, message);
      } else {
        this.setValidity(event, index, message);
      }
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    this.hideAllRemoveHints();
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });

      // data-cart-section を持つ追加セクション（section-cart-campaign など）も同じ経路で更新する
      document.querySelectorAll('[data-cart-section]').forEach((element) => {
        fetch(`${routes.cart_url}?section_id=${element.dataset.cartSection}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const source = html.querySelector('.js-contents');
            const target = element.querySelector('.js-contents');
            if (source && target) target.innerHTML = source.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      });
    }
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];

    // カート内容に応じて表示が変わる追加セクション（section-cart-campaign など）。
    // data-cart-section に section.id を持ち、.js-contents を差し替え対象にする。
    // Section Rendering API は 1 リクエスト 5 セクションまでなので、
    // 超えた分は updateQuantity 後に renderExtraSections で追加取得する。
    document.querySelectorAll('[data-cart-section]').forEach((element) => {
      if (!element.id) return;
      sections.push({
        id: element.id,
        section: element.dataset.cartSection,
        selector: '.js-contents',
      });
    });

    return sections;
  }

  // 空カートで隠す追加セクション（パンくず / テキストバナー / ログイン導線）
  toggleEmptySections(isEmpty) {
    document.querySelectorAll('[data-cart-empty-toggle]').forEach((element) => {
      element.classList.toggle('is-empty', isEmpty);
    });
  }

  // 1 リクエストで取得できるセクション数の上限（Section Rendering API の仕様）
  static SECTIONS_PER_REQUEST = 5;

  // 上限を超えた追加セクションを GET で取り直して .js-contents を差し替える
  renderExtraSections(sections) {
    const limit = CartItems.SECTIONS_PER_REQUEST;
    for (let i = 0; i < sections.length; i += limit) {
      const chunk = sections.slice(i, i + limit);
      const ids = chunk.map((section) => section.section).join(',');
      fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`)
        .then((response) => response.json())
        .then((responseSections) => {
          chunk.forEach((section) => {
            const html = responseSections[section.section];
            const element = document.getElementById(section.id);
            if (!html || !element) return;
            const target = element.querySelector(section.selector) || element;
            target.innerHTML = this.getSectionInnerHTML(html, section.selector);
          });
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  updateQuantity(line, quantity, name, variantId) {
    this.hideAllRemoveHints();
    this.enableLoading(line);

    const sectionsToRender = this.getSectionsToRender();
    const primarySections = sectionsToRender.slice(0, CartItems.SECTIONS_PER_REQUEST);
    const extraSections = sectionsToRender.slice(CartItems.SECTIONS_PER_REQUEST);

    const body = JSON.stringify({
      line,
      quantity,
      sections: primarySections.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        this.toggleEmptySections(parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        primarySections.forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        if (extraSections.length > 0) this.renderExtraSections(extraSections);
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
